import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { AgentStatus, ModelInterpretation } from '@algoboo/schema'
import { request } from '../client'
import { qk } from '../keys'
import { pageOpts } from './paging'

// --- owner home, creators, notifications (milestone 3) ------------------------------

/** GET /me/agents: every agent the caller created, drafts included, with operating state and issues. */
export const myAgentsQuery = (status?: AgentStatus) =>
  infiniteQueryOptions({
    queryKey: qk.myAgents(status ?? null),
    queryFn: ({ pageParam, signal }) => request('listMyAgents', { signal, query: { status, cursor: pageParam, limit: 24 } }),
    ...pageOpts,
  })

export function useMyAgents(status?: AgentStatus, enabled = true) {
  return useInfiniteQuery({ ...myAgentsQuery(status), enabled })
}

export const creatorProfileQuery = (handle: string) => queryOptions({ queryKey: qk.creator(handle), queryFn: ({ signal }) => request('getCreatorProfile', { signal, params: { handle } }) })

/** GET /u/:handle: public creator profile. */
export function useCreatorProfile(handle: string) {
  return useQuery({ ...creatorProfileQuery(handle), enabled: !!handle })
}

/** GET /brains/:id/estimate from the brain's current rate card (admin edits included). */
export function useBrainEstimate(brainId: string | null | undefined, ticksPerDay: number, mode: ModelInterpretation, enabled = true) {
  const ticks = Math.max(1, Math.min(1440, Math.round(ticksPerDay)))
  return useQuery({
    queryKey: qk.brainEstimate(brainId ?? '', ticks, mode),
    queryFn: ({ signal }) => request('getBrainEstimate', { signal, params: { id: brainId! }, query: { ticksPerDay: ticks, mode } }),
    enabled: enabled && !!brainId,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export const notificationsQuery = (unread = false) =>
  infiniteQueryOptions({
    queryKey: qk.notifications(unread),
    queryFn: ({ pageParam, signal }) => request('listMyNotifications', { signal, query: { cursor: pageParam, limit: 30, ...(unread ? { unread: 'true' as const } : {}) } }),
    ...pageOpts,
  })

export function useNotifications(unread = false, enabled = true) {
  return useInfiniteQuery({ ...notificationsQuery(unread), enabled })
}

/** Unread count for the header bell; the notification stream invalidates it on every event. */
export function useUnreadCount(enabled = true) {
  return useQuery({ queryKey: qk.unreadCount(), queryFn: ({ signal }) => request('getMyUnreadCount', { signal }), enabled, refetchInterval: 120_000, retry: false })
}

// --- customer and holder (milestone 3, L4) --------------------------------------

/** GET /me/holdings: agent tokens held by the linked wallets with claimable rewards. */
export function useMyHoldings(enabled = true) {
  return useQuery({ queryKey: qk.myHoldings(), queryFn: ({ signal }) => request('listMyHoldings', { signal }), enabled })
}

export const myInvoicesQuery = (status?: 'open' | 'paid' | 'overdue' | 'void') =>
  queryOptions({ queryKey: qk.myInvoices(status ?? null), queryFn: ({ signal }) => request('listMyInvoices', { signal, query: { status } }) })

/** GET /me/invoices: performance fees and renewals, newest first. */
export function useMyInvoices(status?: 'open' | 'paid' | 'overdue' | 'void', enabled = true) {
  return useQuery({ ...myInvoicesQuery(status), enabled })
}

/** GET /agents/:handle/pricing: the creator's subscription tiers. */
export function useAgentPricing(handle: string, enabled = true) {
  return useQuery({ queryKey: qk.agentPricing(handle), queryFn: ({ signal }) => request('getAgentPricing', { signal, params: { handle } }), enabled: enabled && !!handle })
}
